/* eslint-disable @reatom/unit-naming-rule */
import { action } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { User } from '@root/entities/auth'
import { Network } from '@root/entities/network'
import { TradeSettingsSolRpc } from '@root/entities/trade-settings'
import { TxSerialized } from '@root/entities/transaction'
import { type DelugeId } from '@root/entities/user-short'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { solSelectedRpcAtom } from '@root/features/trade/trade-settings/atoms/reatom-trade-settings-sol-rpc'
import { ApiError, apiRequest } from '@root/shared/api'
import { AuthErrorCode } from '@root/shared/api/api-error-code'

import * as Inviter from '../model/inviter'

import type { TypeHelper } from '@blade/std-lib'
import type { ReferralCreateReferralCodeTransactionCreatePayload } from '@blade/trade-api-client'
import type { InviteCode, ReferralCode } from '@root/entities/referral'
import type { TxSigned } from '@root/entities/transaction'
import type { WalletId } from '@root/entities/wallet'

export interface CreateReferralCodeRequest
  extends TypeHelper.OverrideKeys<
    ReferralCreateReferralCodeTransactionCreatePayload,
    {
      network: Network.Network
      delugeId: DelugeId
      walletId: WalletId
      referralCode: ReferralCode
    }
  > {}

export const referralPrepareCreateTransactionRequestAction = action(
  async (ctx, req: CreateReferralCodeRequest) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.referral.referralCreateReferralCodeTransactionCreate,
        [
          {
            ...req,
            network: Network.toDto(req.network),
          },
        ],
      ),
    )

    const mappedResult = pipe(result)

    return mappedResult
  },
  'withdrawReferralRequestAction',
)

export const referralConfirmCreateTransactionRequestAction = action(
  async (
    ctx,
    req: CreateReferralCodeRequest & {
      signedTx?: TxSigned
      txHash?: string
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.referral
          .referralConfirmCreateReferralCodeTransactionUpdate,
        [
          {
            ...req,
            network: Network.toDto(req.network),
          },
        ],
      ),
    )

    const mappedResult = pipe(
      result,
      Result.map(() => undefined),
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'withdrawReferralRequestAction',
)

export const processCreateReferralCodeTransactionAction = action(
  async (ctx, req: CreateReferralCodeRequest) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)

    const solRpc =
      req.network === Network.Solana ? ctx.get(solSelectedRpcAtom) : undefined

    const prepareResult = await ctx.schedule(() =>
      referralPrepareCreateTransactionRequestAction(
        ctx,
        solRpc
          ? {
              ...req,
              senderKind: TradeSettingsSolRpc.toPathfinderSenderType(solRpc),
            }
          : req,
      ),
    )

    if (Result.isFailure(prepareResult)) {
      return prepareResult
    }

    if (req.network === Network.Solana && solRpc) {
      const sender = TradeSettingsSolRpc.toPathfinderArg(solRpc)

      const sendResult = await ctx.schedule(() =>
        secureStorageService.signAndSendPlainTransaction({
          network: req.network,
          walletId: req.walletId,
          tx: TxSerialized(prepareResult.success.transaction),
          sender,
        }),
      )

      if (Result.isFailure(sendResult)) {
        return sendResult
      }

      return ctx.schedule(() =>
        referralConfirmCreateTransactionRequestAction(ctx, {
          ...req,
          txHash: sendResult.success.signature,
        }),
      )
    }

    const signedTxResult = await ctx.schedule(() =>
      secureStorageService.signPlainTransaction({
        network: req.network,
        walletId: req.walletId,
        tx: TxSerialized(prepareResult.success.transaction),
      }),
    )

    if (Result.isFailure(signedTxResult)) {
      return signedTxResult
    }

    const sendResult = await ctx.schedule(() =>
      referralConfirmCreateTransactionRequestAction(ctx, {
        ...req,
        signedTx: signedTxResult.success.txSigned,
      }),
    )

    return sendResult
  },
)

export const referralCheckAvailabilityRequestAction = action(
  async (
    ctx,
    req: { network: Network.Network; referralCode: ReferralCode },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.referral.referralCheckAvailabilityList, [
        {
          ...req,
          network: Network.toDto(req.network),
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'referralCheckAvailabilityRequestAction',
)

export const userCheckInviterRequestAction = action(
  async (ctx, req: { network: Network.Network; inviteCode: InviteCode }) => {
    const {
      tradeApiClient,
      environment: {
        assets: { avatarsHost },
      },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userCheckInviterList, [
        {
          ...req,
          network: Network.toDto(req.network),
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.map((it) => Inviter.fromDto(it, avatarsHost)),
      Result.mapError((x) => {
        if (ApiError.isOfCode(AuthErrorCode.InviterNotFound)(x)) {
          return x
        }
        return ApiError.fallbackToUnknown(x)
      }),
    )

    return mappedResult
  },
  'userCheckInviterRequestAction',
)

type ChangeReferralCodeRequest = CreateReferralCodeRequest & {
  currentReferralCode: ReferralCode
}

export const referralPrepareChangeTransactionRequestAction = action(
  async (ctx, req: ChangeReferralCodeRequest) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.referral.referralChangeReferralCodeTransactionCreate,
        [
          {
            ...req,
            network: Network.toDto(req.network),
          },
        ],
      ),
    )

    const mappedResult = pipe(result)

    return mappedResult
  },
  'withdrawReferralRequestAction',
)

export const referralConfirmChangeTransactionRequestAction = action(
  async (ctx, req: ChangeReferralCodeRequest & { signedTx: TxSigned }) => {
    const {
      tradeApiClient,
      environment: { assets },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.referral.referralChangeReferralCodeTransactionUpdate,
        [
          {
            ...req,
            network: Network.toDto(req.network),
          },
        ],
      ),
    )

    const mappedResult = pipe(
      result,
      Result.map((it) => User.fromDto(it, assets.avatarsHost)),
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'withdrawReferralRequestAction',
)

export const processChangeReferralCodeTransactionAction = action(
  async (ctx, req: ChangeReferralCodeRequest) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)

    const prepareResult = await ctx.schedule(() =>
      referralPrepareChangeTransactionRequestAction(ctx, req),
    )

    if (Result.isFailure(prepareResult)) {
      return prepareResult
    }

    const signedTxResult = await ctx.schedule(() =>
      secureStorageService.signPlainTransaction({
        network: req.network,
        walletId: req.walletId,
        tx: TxSerialized(prepareResult.success.transaction),
      }),
    )

    if (Result.isFailure(signedTxResult)) {
      return signedTxResult
    }

    const sendResult = await ctx.schedule(() =>
      referralConfirmChangeTransactionRequestAction(ctx, {
        ...req,
        signedTx: signedTxResult.success.txSigned,
      }),
    )

    return sendResult
  },
)
